import { createContext, useContext, useEffect, useState } from "react";
import { AIDrawer } from "../components/ai/AIDrawer";
import { TMessage, TSession } from "../models/ai.model";
import { getActiveSession, getMessages } from "../services/ai.service";
import { TResponse } from "../services/apiWorker";
import { deepCopy } from "../utils/deepCopy";

export const AIContext = createContext<{
  isUsingAI: boolean;
  setUseAI: React.Dispatch<React.SetStateAction<boolean>>;
  isExpandedMode: boolean;
  setExpandedMode: React.Dispatch<React.SetStateAction<boolean>>;

  session?: TSession;

  messageList: TResponse<TMessage>;
  addMessage: (data: TMessage) => void;
}>({
  isUsingAI: false,
  setUseAI: () => {},
  isExpandedMode: false,
  setExpandedMode: () => {},

  session: undefined,

  messageList: TResponse,
  addMessage: (data: TMessage) => {},
});

export function AIProvider({ children }: any) {
  const [isUsingAI, setUseAI] = useState<boolean>(false);
  const [isExpandedMode, setExpandedMode] = useState<boolean>(false);

  const [session, setSession] = useState<TSession>();
  const [messageList, setMessageList] =
    useState<TResponse<TMessage>>(TResponse);

  useEffect(() => {
    getActiveSession({
      onSuccess: (data) => {
        setSession(data);

        getMessages({
          sessionId: data.id,
          onSuccess: (data) => {
            setMessageList({
              ...data,
              content: data.content.map((message) => {
                return {
                  ...message,
                  typing: false,
                };
              }),
            });
          },
        });
      },
      onError: () => {},
    });
  }, []);

  useEffect(() => {
    const tokenDeleted = PubSub.subscribe(
      "sessionDeleted",
      (_, _session: TSession) => {
        if (_session.id == session?.id) {
          setUseAI(false);

          getActiveSession({
            onSuccess: (data) => {
              setSession(data);

              getMessages({
                sessionId: data.id,
                onSuccess: (data) => {
                  setMessageList({
                    ...data,
                    content: data.content.map((message) => {
                      return {
                        ...message,
                        typing: false,
                      };
                    }),
                  });
                },
              });
            },
            onError: () => {},
          });
        }
      }
    );

    const tokenCreated = PubSub.subscribe(
      "sessionCreated",
      (_, _session: TSession) => {
        setSession(_session);

        getMessages({
          sessionId: _session.id,
          onSuccess: (data) => {
            setMessageList({
              ...data,
              content: data.content.map((message) => {
                return {
                  ...message,
                  typing: false,
                };
              }),
            });
          },
        });
      }
    );

    return () => {
      PubSub.unsubscribe(tokenDeleted);
      PubSub.unsubscribe(tokenCreated);
    };
  }, []);

  function addMessage(message: TMessage) {
    setMessageList((prevState) => {
      const copy = deepCopy(prevState);

      copy.content.push(message);

      return copy;
    });
  }

  function updateMessage(oldId: string, message: TMessage) {
    setMessageList((prevState) => {
      const copy = deepCopy(prevState);

      for (let i = 0; i < copy.content.length; i++) {
        if (copy.content[i].id == oldId) {
          copy.content[i] = message;
        }
      }

      return copy;
    });
  }

  return (
    <AIContext.Provider
      value={{
        isUsingAI,
        setUseAI,
        isExpandedMode,
        setExpandedMode,

        session,
        addMessage,
        messageList,
      }}
    >
      {isUsingAI && (
        <AIDrawer
          addMessage={addMessage}
          updateMessage={updateMessage}
          messageList={messageList}
        />
      )}
      {children}
    </AIContext.Provider>
  );
}

export function useAI() {
  const context = useContext(AIContext);

  return context;
}
